import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/age-group-structure.svg";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsappButton";

const HorizontalGroups: React.FC = () => {
  const { t } = useLanguage();

  const ageGroups = [
    {
      title: t("horizontalGroups.groups.baby.title"),
      subtitle: t("horizontalGroups.groups.baby.subtitle"),
      description: t("horizontalGroups.groups.baby.description"),
      image: "👶",
      color: "bg-orange-500",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
      features: t("horizontalGroups.groups.baby.features") || [],
      approach: t("horizontalGroups.groups.baby.approach"),
    },
    {
      title: t("horizontalGroups.groups.toddler.title"),
      subtitle: t("horizontalGroups.groups.toddler.subtitle"),
      description: t("horizontalGroups.groups.toddler.description"),
      image: "🧒",
      color: "bg-blue-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      features: t("horizontalGroups.groups.toddler.features") || [],
      approach: t("horizontalGroups.groups.toddler.approach"),
    },
    {
      title: t("horizontalGroups.groups.preschool.title"),
      subtitle: t("horizontalGroups.groups.preschool.subtitle"),
      description: t("horizontalGroups.groups.preschool.description"),
      image: "👧",
      color: "bg-green-500",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      features: t("horizontalGroups.groups.preschool.features") || [],
      approach: t("horizontalGroups.groups.preschool.approach"),
    },
  ];

  const benefits = [
    {
      title: t("horizontalGroups.benefits.children.title"),
      subtitle: t("horizontalGroups.benefits.children.subtitle"),
      icon: "🌟",
      items: t("horizontalGroups.benefits.children.items") || [],
    },
    {
      title: t("horizontalGroups.benefits.staff.title"),
      subtitle: t("horizontalGroups.benefits.staff.subtitle"),
      icon: "👨‍🏫",
      items: t("horizontalGroups.benefits.staff.items") || [],
    },
  ];

  const whyHorizontalGroups = [
    {
      title: t("horizontalGroups.why.items.safety.title"),
      description: t("horizontalGroups.why.items.safety.description"),
      icon: "🛡️",
      gradient: "bg-amber-500",
    },
    {
      title: t("horizontalGroups.why.items.targeted.title"),
      description: t("horizontalGroups.why.items.targeted.description"),
      icon: "🎯",
      gradient: "bg-blue-500",
    },
    {
      title: t("horizontalGroups.why.items.natural.title"),
      description: t("horizontalGroups.why.items.natural.description"),
      icon: "🌱",
      gradient: "bg-green-500",
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative overflow-hidden hero-section-h">
          <div className="absolute inset-0 pointer-events-none z-0">
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[280px] w-full opacity-100"
            />
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-0 right-0 max-w-[220px] sm:max-w-[280px] md:max-w-[380px] lg:max-w-[500px] w-full opacity-100"
            />
          </div>
          <div className="relative container mx-auto px-4 py-20 z-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-center text-black mb-6">
              {t("horizontalGroups.hero.title")}
            </h1>
            <p className="text-xl md:text-2xl text-center text-natural-900 opacity-90 max-w-4xl mx-auto leading-relaxed">
              {t("horizontalGroups.hero.subtitle")}
              <span className="block text-lg mt-2 opacity-90">
                {t("horizontalGroups.hero.ageRange")}
              </span>
            </p>
          </div>
        </div>

        {/* Introduction Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto">
            <div className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 md:p-12 mb-16">
              <h2 className="text-3xl font-semibold text-neutral-900 mb-6 leading-tight">
                {t("horizontalGroups.intro.title")}
              </h2>
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="p-6">
                  <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                    {t("horizontalGroups.intro.text1")}
                  </p>
                  <p className="text-lg text-neutral-800 leading-relaxed">
                    {t("horizontalGroups.intro.text2")}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-2xl p-6">
                  <h3 className="text-2xl font-semibold text-neutral-900 mb-6">
                    {t("horizontalGroups.intro.threeGroups")}
                  </h3>
                  <div className="space-y-4">
                    {ageGroups.map((group, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-center space-x-4 p-4 bg-[#FBEACD] rounded-xl shadow-sm"
                      >
                        <div
                          className={`w-4 h-4 rounded-full ${group.color}`}
                        ></div>
                        <div className="text-left">
                          <div className="font-semibold text-neutral-900">
                            {group.title}
                          </div>
                          <div className="text-sm text-neutral-800">
                            {group.subtitle}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Horizontal Groups Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-4">
              {t("horizontalGroups.why.title")}
            </h2>
            <p className="text-lg md:text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("horizontalGroups.why.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whyHorizontalGroups.map((item, index) => (
              <div key={index} className="group">
                <div
                  className={`${item.gradient} rounded-2xl p-8 text-white text-center h-full transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl`}
                >
                  <div className="text-5xl mb-6">{item.icon}</div>
                  <h3 className="text-2xl font-semibold mb-4">{item.title}</h3>
                  <p className="text-lg opacity-90 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Group Division Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-4">
              {t("horizontalGroups.groupsDivisionTitle")}
            </h2>
            <p className="text-lg md:text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("horizontalGroups.groupsDivisionSubtitle")}
            </p>
          </div>
          <div className="space-y-12 max-w-7xl mx-auto">
            {ageGroups.map((group, index) => (
              <div
                key={index}
                className={`${group.bgColor} rounded-3xl p-8 border ${group.borderColor} shadow-lg`}
              >
                <div className="grid lg:grid-cols-3 gap-8 items-center">
                  <div className="text-center lg:text-left">
                    <div className="text-6xl mb-4">{group.image}</div>
                    <h3 className="text-3xl font-semibold text-neutral-900 mb-2">
                      {group.title}
                    </h3>
                    <div
                      className={`inline-block ${group.color} text-white px-4 py-2 rounded-full text-sm font-semibold mb-4`}
                    >
                      {group.subtitle}
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      {group.description}
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-[#FBEACD] rounded-xl p-6 shadow-sm">
                        <h4 className="text-xl font-semibold text-neutral-900 mb-4">
                          {t("horizontalGroups.whatChildrenReceive")}
                        </h4>
                        <ul className="space-y-3">
                          {Array.isArray(group.features)
                            ? group.features.map(
                                (feature: string, idx: number) => (
                                  <li
                                    key={idx}
                                    className="flex items-start text-left space-x-3"
                                  >
                                    <div
                                      className={`w-2 h-2 ${group.color} rounded-full mt-2 flex-shrink-0`}
                                    ></div>
                                    <span className="text-gray-700">
                                      {feature}
                                    </span>
                                  </li>
                                )
                              )
                            : null}
                        </ul>
                      </div>
                      <div className="bg-[#FBEACD] rounded-xl p-6 shadow-sm">
                        <h4 className="text-xl font-semibold text-neutral-900 mb-4">
                          {t("horizontalGroups.ourApproach")}
                        </h4>
                        <p className="text-gray-700 leading-relaxed">
                          {group.approach}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-4">
              {t("horizontalGroups.benefits.title")}
            </h2>
            <p className="text-lg md:text-xl text-neutral-800 max-w-3xl mx-auto">
              {t("horizontalGroups.benefits.subtitle")}
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-[#FBEACD] rounded-3xl shadow-xl p-8 hover:shadow-2xl transition-shadow duration-300"
              >
                <span className="text-4xl">{benefit.icon}</span>
                <div className="flex items-center justify-start space-x-4 mb-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-semibold text-neutral-900">
                      {benefit.title}
                    </h3>
                    <p className="text-neutral-800">{benefit.subtitle}</p>
                  </div>
                </div>
                <ul className="space-y-4">
                  {Array.isArray(benefit.items)
                    ? benefit.items.map((item: string, idx: number) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-[#492A19] rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700 leading-relaxed text-left">
                            {item}
                          </span>
                        </li>
                      ))
                    : null}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Transitions Section */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#FBEACD] rounded-3xl shadow-2xl p-12 text-black relative overflow-hidden">
              <div className="relative">
                <h2 className="text-3xl font-semibold text-center mb-8">
                  {t("horizontalGroups.transitions.title")}
                </h2>
                <p className="text-lg mb-8 text-center opacity-90 leading-relaxed">
                  {t("horizontalGroups.transitions.subtitle")}
                </p>
                <div className="grid md:grid-cols-2 gap-8">
                  {Array.isArray(t("horizontalGroups.transitions.items"))
                    ? t("horizontalGroups.transitions.items").map(
                        (item: string, idx: number) => (
                          <div
                            key={idx}
                            className="space-x-4 p-4 bg-[#FEF8EE] bg-opacity-100 rounded-xl backdrop-blur-sm text-center"
                          >
                            <span className="text-md">{item}</span>
                          </div>
                        )
                      )
                    : null}
                </div>
                <p className="text-center mt-8 text-lg opacity-90">
                  {t("horizontalGroups.transitions.conclusion")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        {/* <div className="container mx-auto px-4 py-16 font-child">
          <div className="text-center">
            <div className="rounded-3xl p-12 max-w-4xl mx-auto">
              <h3 className="text-3xl font-semibold text-neutral-900 mb-4">
                {t("horizontalGroups.cta.title")}
              </h3>
              <p className="text-xl text-neutral-800 mb-8 max-w-2xl mx-auto leading-relaxed">
                {t("horizontalGroups.cta.subtitle")}
              </p>
              <button
                className="bg-[#FBEACD] text-neutral-900 px-8 py-3 rounded-full font-semibold text-lg hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
              >
                {t("horizontalGroups.cta.button")}
              </button>
            </div>
          </div>
        </div> */}
      </div>

      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      {/* Footer Section */}
      <Footer />
    </div>
  );
};

export default HorizontalGroups;
